// src/components/Finanzas/components/RevenueBarChart.jsx
// Ingreso per day / week / month as hand-rolled inline SVG.
//
// Takes pre-aggregated buckets — [{ bucket, facturas, ingreso }] — that the
// server already grouped (finanzas_buckets for days/weeks, v_finanzas_mensual
// for months). This component only lays them out: it fills empty buckets so
// the axis has no gaps and scales bar heights against the largest bucket. It
// never adds amounts together.

import React from 'react';
import { fmtUsd } from '../utils/facturaMath';
import { addDaysStr, calendarMonthRange, formatSvDate } from '../utils/timeBoundaries';

const GRANO_OPTIONS = [
  { key: 'day',   label: 'Diario' },
  { key: 'week',  label: 'Semanal' },
  { key: 'month', label: 'Mensual' },
];

const W = 720;
const H = 240;
const PAD_L = 76;
const PAD_R = 8;
const PAD_T = 14;
const PAD_B = 30;
const PLOT_W = W - PAD_L - PAD_R;
const PLOT_H = H - PAD_T - PAD_B;

// Monday of the ISO week containing dateStr — matches Postgres date_trunc('week').
const mondayOf = (dateStr) => {
  const dow = new Date(`${dateStr}T00:00:00Z`).getUTCDay(); // 0 = domingo
  return addDaysStr(dateStr, -((dow + 6) % 7));
};

// Every bucket key between desde and hasta, so days with no invoices still get a slot.
const bucketKeys = (desde, hasta, grano) => {
  const keys = [];
  if (!desde || !hasta) return keys;
  if (grano === 'day') {
    for (let d = desde; d <= hasta; d = addDaysStr(d, 1)) keys.push(d);
  } else if (grano === 'week') {
    for (let d = mondayOf(desde); d <= hasta; d = addDaysStr(d, 7)) keys.push(d);
  } else {
    for (let d = calendarMonthRange(desde).startStr; d <= hasta; d = addDaysStr(calendarMonthRange(d).endStr, 1)) keys.push(d);
  }
  return keys;
};

const shortLabel = (key, grano) => {
  if (grano === 'day') return String(Number(key.slice(8, 10)));
  if (grano === 'week') return `${key.slice(8, 10)}/${key.slice(5, 7)}`;
  const { label } = calendarMonthRange(key);
  return `${label.slice(0, 3)} ${key.slice(2, 4)}`;
};

const longLabel = (key, grano) => {
  if (grano === 'day') return formatSvDate(key);
  if (grano === 'week') return `Semana del ${formatSvDate(key)}`;
  return calendarMonthRange(key).label;
};

const GRANO_NOUN = { day: 'día', week: 'semana', month: 'mes' };

const RevenueBarChart = ({ rows, desde, hasta, grano, onGranoChange, periodLabel, loading, error, onRetry }) => {
  const byKey = new Map();
  for (const r of rows || []) byKey.set(String(r.bucket).slice(0, 10), r);

  const keys = bucketKeys(desde, hasta, grano);
  let max = 0;
  for (const k of keys) {
    const v = Number(byKey.get(k)?.ingreso) || 0;
    if (v > max) max = v;
  }

  const slot = keys.length > 0 ? PLOT_W / keys.length : PLOT_W;
  const barW = Math.max(2, slot * 0.7);
  const labelEvery = Math.max(1, Math.ceil(keys.length / 16));
  const titleId = `fin-bar-title-${grano}`;

  return (
    <div className="fin-card fin-chart">
      <div className="fin-chart-head">
        <div className="fin-card-label">Ingreso por {GRANO_NOUN[grano]}</div>
        <div className="fin-seg" role="group" aria-label="Agrupar ingreso por">
          {GRANO_OPTIONS.map((o) => (
            <button
              key={o.key}
              type="button"
              className={`fin-seg-btn ${grano === o.key ? 'is-active' : ''}`}
              aria-pressed={grano === o.key}
              onClick={() => onGranoChange(o.key)}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>
      <div className="fin-card-period">{grano === 'month' ? `Hasta ${periodLabel}` : periodLabel}</div>

      {error && (
        <div className="fin-banner fin-banner--error" role="alert">
          <span>{error}</span>
          {onRetry && <button type="button" className="fin-btn" onClick={onRetry}>Reintentar</button>}
        </div>
      )}
      {!error && loading && <div className="fin-muted">Cargando gráfica…</div>}
      {!error && !loading && (
        <svg className="fin-svg" viewBox={`0 0 ${W} ${H}`} role="img" aria-labelledby={titleId} preserveAspectRatio="none">
          <title id={titleId}>{`Ingreso por ${GRANO_NOUN[grano]} — ${periodLabel}`}</title>

          {/* Axis: only the two values the data supplies — zero and the largest bucket. */}
          <line className="fin-axis-line" x1={PAD_L} x2={W - PAD_R} y1={PAD_T} y2={PAD_T} />
          <line className="fin-axis-line" x1={PAD_L} x2={W - PAD_R} y1={PAD_T + PLOT_H} y2={PAD_T + PLOT_H} />
          <text className="fin-axis-text" x={PAD_L - 6} y={PAD_T + 4} textAnchor="end">{fmtUsd(max)}</text>
          <text className="fin-axis-text" x={PAD_L - 6} y={PAD_T + PLOT_H} textAnchor="end">{fmtUsd(0)}</text>

          {keys.map((k, i) => {
            const row = byKey.get(k);
            const v = Number(row?.ingreso) || 0;
            const h = max > 0 ? (v / max) * PLOT_H : 0;
            const x = PAD_L + i * slot + (slot - barW) / 2;
            const tip = row
              ? `${longLabel(k, grano)}: ${fmtUsd(row.ingreso)} · ${row.facturas} ${Number(row.facturas) === 1 ? 'factura' : 'facturas'}`
              : `${longLabel(k, grano)}: sin facturas`;
            return (
              <g key={k}>
                <rect className="fin-bar" x={x} y={PAD_T + PLOT_H - h} width={barW} height={Math.max(h, v > 0 ? 1 : 0)}>
                  <title>{tip}</title>
                </rect>
                {/* Transparent hit area so empty buckets still show their tooltip. */}
                <rect x={PAD_L + i * slot} y={PAD_T} width={slot} height={PLOT_H} fill="transparent">
                  <title>{tip}</title>
                </rect>
                {i % labelEvery === 0 && (
                  <text className="fin-axis-text" x={PAD_L + i * slot + slot / 2} y={H - 10} textAnchor="middle">
                    {shortLabel(k, grano)}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      )}
    </div>
  );
};

export default RevenueBarChart;
